import { forwardRef, Children, useMemo, memo } from 'react'
import PropTypes from 'prop-types'
import { text as textColors } from 'theme/palette'
import { fontWeight, fontFamily } from 'theme/typography'
import { get } from 'lodash'
import Typography from '@mui/material/Typography'
import useTheme from '@mui/material/styles/useTheme'

const MIXINS = {
  gradient: {
    background: 'linear-gradient(277.58deg, #DAF3F5 30.02%, #FFFFFF 67.12%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textFillColor: 'transparent',
  },
}

const Type = forwardRef((props, ref) => {
  const theme = useTheme()
  const {
    variant,
    as,
    color,
    weight,
    blend,
    wrap,
    italic,
    inline,
    lines,
    selectable,
    hover,
    textDecoration,
    fontFamily,
    children,
    isLoading,
    mixin,
    sx,
    ...otherProps
  } = props

  const sxProps = useMemo(
    () => ({
      ...(mixin && MIXINS[mixin]),
      color: !!color
        ? color?.includes('text.')
          ? color
          : `text.${color}`
        : undefined,
      fontWeight: weight
        ? `${theme.globals.fontWeight[weight]} !important`
        : undefined,
      opacity: blend
        ? get(theme, `globals.opacity.${blend}`, theme.globals.opacity.main)
        : undefined,
      whiteSpace: wrap,
      fontStyle: italic ? 'italic' : undefined,
      display: inline ? 'inline' : undefined,
      cursor: selectable ? 'pointer' : undefined,
      textDecoration,
      ...(lines && {
        display: '-webkit-box',
        '-webkit-line-clamp': lines,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
      ...(hover && {
        '&:hover': {
          color:
            typeof hover === 'string'
              ? theme.palette.text[hover]
              : hover && color
              ? theme.palette.text[`${color}Hover`]
              : undefined,
        },
      }),
      transition: theme.internalMixins.setTransition(['opacity', 'color']),
      ...(fontFamily && {
        fontFamily: theme.globals.fontFamily[fontFamily],
      }),
    }),
    [
      color,
      weight,
      blend,
      wrap,
      italic,
      inline,
      lines,
      selectable,
      hover,
      textDecoration,
      fontFamily,
      mixin,
    ]
  )

  return (
    <Typography
      ref={ref}
      variant={variant}
      as={as ? as : variant === 'inherit' ? 'span' : 'div'}
      sx={{
        ...sxProps,
        ...(typeof sx === 'function' ? sx(theme) : sx),
      }}
      {...(props.onMouseDown || props.onMouseUp || props.onClick) && {
        onKeyUp: e => {
          if (e.keyCode === 13) {
            props.onMouseDown?.(e)
            props.onMouseUp?.(e)
            props.onClick?.(e)
          }
        },
        tabIndex: '0',
      }}
      {...otherProps}
    >
      {Children.toArray(children)}
    </Typography>
  )
})

Type.propTypes = {
  variant: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  weight: PropTypes.oneOf(Object.keys(fontWeight)),
  color: PropTypes.oneOf(Object.keys(textColors)),
  mixin: PropTypes.oneOf(Object.keys(MIXINS)),
  hover: PropTypes.oneOf([
    ...Object.keys(textColors),
    true,
    false,
    undefined,
  ]),
  blend: PropTypes.any,
  // blend: PropTypes.oneOf([true, false, ...Object.keys(opacity)]),
  isLoading: PropTypes.bool,
  lines: PropTypes.string,
  italic: PropTypes.bool,
  fontFamily: PropTypes.oneOf(Object.keys(fontFamily)),
}

Type.defaultProps = {
  variant: 'body',
  isLoading: false,
  sx: {},
}

export default memo(Type)
